import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'https://profile.hitip.app'

const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
})

/** Kiosk (/api/kiosk/...) — no JWT and no refresh interceptor (public PIN session only). */
export const kioskHttp = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
})

const refreshClient = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

let isRefreshing = false
const failedQueue: Array<{ resolve: (token: string | null) => void; reject: (err: unknown) => void }> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error)
    else prom.resolve(token)
  })
  failedQueue.length = 0
}

api.interceptors.request.use((config) => {
  if (!config.skipAuthHeader) {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  // Default instance sets application/json; FormData needs multipart boundary from the browser/axios.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Credential errors (wrong email/password): must not run refresh-queue logic.
    if (error.response?.status === 401 && originalRequest?.skipAuthRefresh) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest.__isRetryRequest) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = 'Bearer ' + token
          return api(originalRequest)
        })
      }

      originalRequest.__isRetryRequest = true
      isRefreshing = true

      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        return Promise.reject(error)
      }

      try {
        const { data } = await refreshClient.post('/api/token/refresh/', { refresh: refreshToken })

        localStorage.setItem('access_token', data.access)
        processQueue(null, data.access)

        originalRequest.headers.Authorization = 'Bearer ' + data.access
        return api(originalRequest)
      } catch (err) {
        processQueue(err, null)
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        const base = import.meta.env.BASE_URL ?? '/'
        const prefix = base.endsWith('/') ? base : `${base}/`
        window.location.href = `${prefix}auth/login`
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

const baseURL = api.defaults.baseURL || ''

// Legacy URL helpers for data/pages that use fetch() with URLs
type ApiWithUrls = typeof api & {
  allProjects(): string
  project(id: string): string
  allUsers(): string
  user(id: string | number): string
  avatars(): string
}
const apiWithUrls = api as ApiWithUrls
apiWithUrls.allProjects = () => `${baseURL}/projects`
apiWithUrls.project = (id: string) => `${baseURL}/projects/${id}`
apiWithUrls.allUsers = () => `${baseURL}/users`
apiWithUrls.user = (id: string | number) => `${baseURL}/users/${id}`
apiWithUrls.avatars = () => `${baseURL}/users/avatars`

export default apiWithUrls
